// src/components/Common/Carousel.tsx
import React from 'react';
import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CarouselProps {
  title: string;
  description?: string;
  children: ReactNode;
  id: string;
  className?: string;
  itemWidth?: number; // Width of each item in pixels (default: 320)
  scrollAmount?: number; // Number of items to scroll at once (default: 3)
}

const Carousel: React.FC<CarouselProps> = ({
  title,
  description,
  children,
  id,
  className = '',
  itemWidth = 320,
  scrollAmount = 3,
}) => {
  const handleScroll = (direction: 'left' | 'right') => {
    const scrollContainer = document.getElementById(`carousel-${id}`);
    if (scrollContainer) {
      const scrollDistance = itemWidth * scrollAmount;
      
      if (direction === 'left') {
        scrollContainer.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
      } else {
        scrollContainer.scrollBy({ left: scrollDistance, behavior: 'smooth' });
      }
    }
  };

  return (
    <div className={`mb-6 ${className}`}>
      {/* Section Header */}
      <div className="px-4 sm:px-6 lg:px-8 mb-3">
        <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>
        {description && (
          <p className="text-gray-600 text-sm">{description}</p>
        )}
      </div>

      {/* Carousel Container */}
      <div className="relative group">
        {/* Left Navigation Button */}
        <button
          onClick={() => handleScroll('left')}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-30 p-2 sm:p-3 rounded-full bg-black/50 hover:bg-black/70 text-white opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 shadow-lg backdrop-blur-sm"
          aria-label={`Scroll ${title} left`}
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        
        {/* Right Navigation Button */}
        <button
          onClick={() => handleScroll('right')}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-30 p-2 sm:p-3 rounded-full bg-black/50 hover:bg-black/70 text-white opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 shadow-lg backdrop-blur-sm"
          aria-label={`Scroll ${title} right`}
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        
        {/* Scrollable Content */}
        <div
          id={`carousel-${id}`}
          className="carousel-container flex overflow-x-auto space-x-4 px-2 sm:px-4 lg:px-6 pb-4 scroll-smooth"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default Carousel;